import React, { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  FormControl,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getNavRenewalReport } from "../../services/Dashboard";
import { isValidDate } from "../../services/Utils";
import SearchIcon from "@mui/icons-material/Search";
import PublicIcon from "@mui/icons-material/Public";
import LaptopIcon from "@mui/icons-material/Laptop";
import { getColumnsData, TIMEZONE } from "./NavRenewalReportColumns";
import OTAPagination from "../OTAPagination";

// ─── Summary header metrics ───────────────────────────────────────────────────

const SummaryHeader = ({ summary }) => {
  if (!summary) return null;
  const daysAdded = summary.daysAdded || 0;
  const daysRemoved = summary.daysRemoved || 0;
  const netDays = summary.netDays ?? (daysAdded + daysRemoved);

  const metrics = [
    {
      label: "Days Added",
      value: `+${daysAdded}`,
      color: "success",
    },
    {
      label: "Days Removed",
      value: daysRemoved,
      color: "error",
    },
    {
      label: "Net Days",
      value: netDays >= 0 ? `+${netDays}` : `${netDays}`,
      color: netDays >= 0 ? "success" : "error",
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {metrics.map((m) => (
        <Grid item xs={12} sm={4} key={m.label}>
          <Card
            variant="outlined"
            sx={{
              textAlign: "center",
              py: 1.5,
              px: 2,
              bgcolor: "background.default",
            }}
          >
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--mui-palette-text-secondary, #666)",
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {m.label}
            </div>
            <div
              style={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color:
                  m.color === "success"
                    ? "#2e7d32"
                    : m.color === "error"
                      ? "#c62828"
                      : "inherit",
              }}
            >
              {m.value}
            </div>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const NavRenewalReport = (props) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [pickerInputErrorText, setPickerInputErrorText] = useState(null);
  const [timezone, setTimezone] = useState(TIMEZONE.LOCAL);
  const [summary, setSummary] = useState(null);

  const [searchParams, setSearchParams] = useState(null);

  const columns = getColumnsData(timezone);

  const processSearch = () => {
    if (!isValidDate(new Date(fromDate)) || !isValidDate(new Date(toDate))) {
      setPickerInputErrorText("Please select valid From and To dates.");
      return;
    }
    if (fromDate > toDate) {
      setPickerInputErrorText("From Date must be before To Date.");
      return;
    }
    setPickerInputErrorText(null);
    const from = new Date(fromDate);
    from.setHours(0, 0, 0, 0);
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999);
    setSearchParams({
      fromDate: from.toISOString(),
      toDate: to.toISOString(),
    });
  };

  return (
    <div className="fragmentContainer">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Card sx={{ height: "96px" }}>
              <CardContent className="tile-cards">
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <FormControl variant="standard" sx={{ minWidth: 150 }}>
                    <DatePicker
                      disableFuture
                      label="From Date"
                      views={["year", "month", "day"]}
                      value={fromDate}
                      timezone="UTC"
                      onChange={(val) => {
                        if (!isValidDate(new Date(val))) {
                          setPickerInputErrorText("Invalid From Date!");
                          return;
                        }
                        setPickerInputErrorText(null);
                        setFromDate(val);
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          id="renewal-from-date"
                          variant="standard"
                          onKeyDown={(e) => e.preventDefault()}
                          error={
                            !!pickerInputErrorText &&
                            pickerInputErrorText.includes("From")
                          }
                        />
                      )}
                      maxDate={toDate || new Date()}
                      minDate={new Date("01-01-2023")}
                      required
                    />
                  </FormControl>

                  <FormControl variant="standard" sx={{ minWidth: 150 }}>
                    <DatePicker
                      disableFuture
                      label="To Date"
                      views={["year", "month", "day"]}
                      value={toDate}
                      timezone="UTC"
                      onChange={(val) => {
                        if (!isValidDate(new Date(val))) {
                          setPickerInputErrorText("Invalid To Date!");
                          return;
                        }
                        setPickerInputErrorText(null);
                        setToDate(val);
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          id="renewal-to-date"
                          variant="standard"
                          onKeyDown={(e) => e.preventDefault()}
                          error={
                            !!pickerInputErrorText &&
                            pickerInputErrorText.includes("To")
                          }
                        />
                      )}
                      maxDate={new Date()}
                      minDate={fromDate || new Date("01-01-2023")}
                      required
                    />
                  </FormControl>

                  <Button
                    disabled={
                      !!pickerInputErrorText || !fromDate || !toDate
                    }
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={processSearch}
                  >
                    Search
                  </Button>

                  <Tooltip
                    title={
                      timezone === TIMEZONE.LOCAL
                        ? "Currently showing browser local time. Click to switch to UTC."
                        : "Currently showing UTC. Click to switch to local time."
                    }
                  >
                    <ToggleButtonGroup
                      value={timezone}
                      exclusive
                      size="small"
                      onChange={(_, val) => {
                        if (val) setTimezone(val);
                      }}
                      aria-label="Timezone"
                    >
                      <ToggleButton value={TIMEZONE.LOCAL} aria-label="Local time">
                        <LaptopIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Local
                      </ToggleButton>
                      <ToggleButton value={TIMEZONE.UTC} aria-label="UTC">
                        <PublicIcon fontSize="small" sx={{ mr: 0.5 }} />
                        UTC
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Tooltip>

                  {pickerInputErrorText && (
                    <div
                      style={{
                        color: "#d32f2f",
                        fontSize: "0.75rem",
                        width: "100%",
                      }}
                    >
                      {pickerInputErrorText}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </Grid>
          {summary && (
            <Grid item xs={12} className="no-export">
              <SummaryHeader summary={summary} />
            </Grid>
          )}

          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ padding: 0, ":last-child": { padding: 0 } }}>
                <OTAPagination
                  key={`${searchParams?.fromDate || ""}_${searchParams?.toDate || ""}_${timezone}`}
                  columns={columns}
                  endpointFn={getNavRenewalReport}
                  queryName="fetchNavRenewalReport"
                  sortingMode="server"
                  filterMode="server"
                  sx={{ height: "calc(100vh - 200px)" }}
                  sortByDefault="createdAt"
                  extraFilters={searchParams}
                  getRowId={(row) =>
                    row?.subscriptionAccountingId ||
                    `${row?.serialNo || ""}-${row?.vehicleKey || ""}-${row?.createdAt || ""}`
                  }
                  exportCSV
                  tableTitle="Navigation Renewal Report"
                  onSuccess={(response) => {
                    setSummary(response?.data?.summary || null);
                  }}
                  {...props}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </LocalizationProvider>
    </div>
  );
};

export default NavRenewalReport;
